import type { PivotColumn, PivotRow } from './PivotTable';
import {
  type CardProblem,
  type CardResult,
  DATE_KEY,
  type ValueFormat,
  isProblem,
  num,
  pivot,
  ratio,
  text,
} from './cards';

export type Grain = 'day' | 'month' | 'quarter' | 'year';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A bucket's first day as the workbook labels it: 19 Oct 2026, Oct 2026, Q4 2026, 2026. */
export function bucketLabel(value: unknown, grain: Grain): string {
  const day = text(value).slice(0, 10);
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month) {
    return day;
  }
  if (grain === 'year') {
    return String(year);
  }
  if (grain === 'quarter') {
    return `Q${Math.floor((month - 1) / 3) + 1} ${year}`;
  }
  if (grain === 'month') {
    return `${MONTHS[month - 1]} ${year}`;
  }
  return `${date} ${MONTHS[month - 1]} ${year}`;
}

export type Crosstab = { columns: Array<PivotColumn>; rows: Array<PivotRow>; total?: PivotRow };

/**
 * Rows by a dimension and one column per date bucket. `value` names one summed measure, or two whose
 * ratio is taken per cell, so ratios are never summed. Totals only when the measure is additive:
 * distinct counts (orders, items) cannot be added across rows or buckets.
 */
export function crosstab(
  result: CardResult | CardProblem,
  rowKey: string,
  grain: Grain,
  format: ValueFormat,
  value: { measure: string } | { top: string; base: string },
  additive = true,
): Crosstab {
  const rows = isProblem(result) ? [] : result.rows;
  const read = (key: string) => (row: Record<string, unknown>) => num(row[key]);
  const main = pivot(rows, rowKey, DATE_KEY, read('measure' in value ? value.measure : value.top));
  const base = 'base' in value ? pivot(rows, rowKey, DATE_KEY, read(value.base)) : null;
  const cell = (top: number | null, bottom: number | null) => (base ? ratio(top, bottom) : top);
  const columns: Array<PivotColumn> = [
    ...main.columns.map((column) => ({ key: column, label: bucketLabel(column, grain), format })),
    ...(additive ? [{ key: 'total', label: 'Total', format }] : []),
  ];
  const lines = main.rows.map((row) => ({
    label: row,
    values: {
      ...Object.fromEntries(
        main.columns.map((column) => [column, cell(main.cell(row, column), base?.cell(row, column) ?? null)]),
      ),
      ...(additive ? { total: cell(main.rowTotal(row), base?.rowTotal(row) ?? null) } : {}),
    },
  }));
  if (!additive) {
    return { columns, rows: lines };
  }
  const grand = (getter: (column: string) => number | null) =>
    main.columns.reduce<number | null>((sum, column) => {
      const amount = getter(column);
      return amount === null ? sum : (sum ?? 0) + amount;
    }, null);
  const total: PivotRow = {
    label: 'Total',
    values: {
      ...Object.fromEntries(
        main.columns.map((column) => [column, cell(main.columnTotal(column), base?.columnTotal(column) ?? null)]),
      ),
      total: cell(grand(main.columnTotal), base ? grand(base.columnTotal) : null),
    },
  };
  return { columns, rows: lines, total };
}

/** Each cell as a share of its column total: the workbook's percent-of-total table calculation. */
export function shareOfColumn(table: Crosstab): Crosstab {
  const totals = table.total?.values ?? {};
  const share = (row: PivotRow): PivotRow => ({
    label: row.label,
    values: Object.fromEntries(
      Object.entries(row.values).map(([key, amount]) => [key, ratio(amount, totals[key] ?? null)]),
    ),
  });
  return {
    columns: table.columns.map((column) => ({ ...column, format: 'percent' })),
    rows: table.rows.map(share),
    total: table.total && share(table.total),
  };
}
